// src/app/shared/notifications/notifications-nav/notifications-nav.component.ts

import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  QueryList,
  ViewChildren,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { Observable, map } from 'rxjs';
import { filter } from 'rxjs/operators';

import { RequiredNotificationsService } from '../notifications/notifications.service';

@Component({
  selector: 'notifications-nav',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './notifications-nav.component.html',
  styleUrls: ['./notifications-nav.component.scss'],
})
export class NotificationsNavComponent implements AfterViewInit {
  @ViewChildren('navLink', { read: ElementRef })
  navLinks!: QueryList<ElementRef<HTMLAnchorElement>>;

  private readonly router = inject(Router);
  private readonly requiredNotificationsService = inject(RequiredNotificationsService);

  isHidden = false;
  private lastScrollY = 0;

  readonly timeOffCount$: Observable<number> =
    this.requiredNotificationsService.notifications$.pipe(
      map((notifications) => notifications.dayOffRequestGroupsNeedToApproveBySupervisor?.length ?? 0),
    );

  readonly supervisorHoursCount$: Observable<number> =
    this.requiredNotificationsService.notifications$.pipe(
      map((notifications) => notifications.bookedHoursNeedToApproveBySupervisor?.length ?? 0),
    );

  readonly myHoursCount$: Observable<number> =
    this.requiredNotificationsService.notifications$.pipe(
      map((notifications) => notifications.bookedHoursNeedToApproveBySelf?.length ?? 0),
    );

  ngAfterViewInit(): void {
    setTimeout(() => this.scrollToActive(false), 0);

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => {
        setTimeout(() => this.scrollToActive(true), 0);
      });

    this.navLinks.changes.subscribe(() => {
      setTimeout(() => this.scrollToActive(false), 0);
    });
  }

  @HostListener('window:scroll')
  onScroll(): void {
    const current = window.scrollY;

    if (current < 30) {
      this.isHidden = false;
      this.lastScrollY = current;
      return;
    }

    if (current > this.lastScrollY) {
      this.isHidden = true;
    } else {
      this.isHidden = false;
      setTimeout(() => this.scrollToActive(false), 0);
    }

    this.lastScrollY = current;
  }

  formatBadgeCount(count: number | null | undefined): string {
    const safeCount = Number(count ?? 0);
    return safeCount > 99 ? '99+' : String(safeCount);
  }

  private scrollToActive(smooth = true): void {
    if (!this.navLinks?.length) return;

    const activeLink = this.navLinks.find((link) =>
      link.nativeElement.classList.contains('active'),
    );

    if (!activeLink) return;

    activeLink.nativeElement.scrollIntoView({
      behavior: smooth ? 'smooth' : 'auto',
      inline: 'center',
      block: 'nearest',
    });
  }
}